import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { csvAPI, downloadCSV } from '../lib/api';
import { toast } from 'sonner';
import {
  Download,
  Upload,
  FileSpreadsheet,
  Scissors,
  Package,
  Users,
  Calendar,
  Clock,
  CalendarX,
  Tag,
  CheckCircle,
  AlertCircle,
  Loader2,
  FileDown,
  FileUp,
  Info
} from 'lucide-react';

// Entity configurations
const ENTITIES = [
  {
    id: 'categories',
    name: 'Categorie',
    icon: Tag,
    description: 'Categorie per servizi e prodotti',
    fields: ['name', 'description', 'type (service/product)'],
    exportFn: csvAPI.exportCategories,
    templateFn: csvAPI.exportCategoriesTemplate,
    importFn: csvAPI.importCategories,
  },
  {
    id: 'services',
    name: 'Servizi',
    icon: Scissors,
    description: 'Lista servizi offerti',
    fields: ['name', 'description', 'duration_minutes', 'price', 'category_name', 'active'],
    exportFn: csvAPI.exportServices,
    templateFn: csvAPI.exportServicesTemplate,
    importFn: csvAPI.importServices,
    note: 'La categoria deve esistere prima di importare i servizi',
  },
  {
    id: 'products',
    name: 'Prodotti',
    icon: Package,
    description: 'Prodotti in vendita nello shop',
    fields: ['name', 'description', 'price', 'stock', 'category_name', 'image_url', 'active'],
    exportFn: csvAPI.exportProducts,
    templateFn: csvAPI.exportProductsTemplate,
    importFn: csvAPI.importProducts,
    note: 'La categoria deve esistere prima di importare i prodotti',
  },
  {
    id: 'staff',
    name: 'Staff',
    icon: Users,
    description: 'Operatori/dipendenti',
    fields: ['name', 'email', 'phone', 'specializations (separate da |)'],
    exportFn: csvAPI.exportStaff,
    templateFn: csvAPI.exportStaffTemplate,
    importFn: csvAPI.importStaff,
    note: 'Password default: Staff123! - Verranno creati turni Lun-Sab 9-18',
  },
  {
    id: 'clients',
    name: 'Clienti',
    icon: Users,
    description: 'Anagrafica clienti',
    fields: ['name', 'email', 'phone'],
    exportFn: csvAPI.exportClients,
    templateFn: csvAPI.exportClientsTemplate,
    importFn: csvAPI.importClients,
    note: 'Password default: Welcome123!',
  },
  {
    id: 'appointments',
    name: 'Appuntamenti',
    icon: Calendar,
    description: 'Prenotazioni',
    fields: ['date', 'time', 'client_email', 'client_name', 'client_phone', 'service_name', 'staff_email', 'notes', 'status'],
    exportFn: csvAPI.exportAppointments,
    templateFn: csvAPI.exportAppointmentsTemplate,
    importFn: csvAPI.importAppointments,
    note: 'Servizi e staff devono esistere. Status: IN_ATTESA, CONFERMATO, COMPLETATO, ANNULLATO',
  },
  {
    id: 'store-hours',
    name: 'Orari Negozio',
    icon: Clock,
    description: 'Orari apertura/chiusura',
    fields: ['day_of_week', 'open_time', 'close_time', 'is_closed'],
    exportFn: csvAPI.exportStoreHours,
    templateFn: csvAPI.exportStoreHoursTemplate,
    importFn: csvAPI.importStoreHours,
    note: 'Giorni: monday, tuesday, wednesday, thursday, friday, saturday, sunday',
  },
  {
    id: 'closures',
    name: 'Chiusure',
    icon: CalendarX,
    description: 'Giorni di chiusura straordinaria',
    fields: ['date (YYYY-MM-DD)', 'reason'],
    exportFn: csvAPI.exportClosures,
    templateFn: csvAPI.exportClosuresTemplate,
    importFn: csvAPI.importClosures,
  },
];

export default function ImportExportSection({ onDataChange }) {
  const [loading, setLoading] = useState({});
  const [resultDialog, setResultDialog] = useState({ open: false, title: '', result: null });
  const fileInputRefs = useRef({});

  const handleExport = async (entity, isTemplate = false) => {
    const key = `export-${entity.id}${isTemplate ? '-template' : ''}`;
    setLoading(prev => ({ ...prev, [key]: true }));
    
    try {
      const fn = isTemplate ? entity.templateFn : entity.exportFn;
      const response = await fn();
      const filename = isTemplate 
        ? `${entity.id}_template.csv` 
        : `${entity.id}_export_${new Date().toISOString().split('T')[0]}.csv`;
      downloadCSV(response.data, filename);
      toast.success(`${isTemplate ? 'Template' : 'Dati'} ${entity.name} scaricato`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Errore export ${entity.name}`);
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleImport = async (entity, file) => {
    if (!file) return;
    
    const key = `import-${entity.id}`;
    setLoading(prev => ({ ...prev, [key]: true }));
    
    try {
      const response = await entity.importFn(file);
      const result = response.data;
      
      setResultDialog({
        open: true,
        title: `Import ${entity.name}`,
        result: result
      });
      
      if (result.imported > 0) {
        toast.success(`${result.imported} record importati`);
        if (onDataChange) onDataChange();
      }
      
      if (result.errors?.length > 0) {
        toast.warning(`${result.errors.length} errori durante l'import`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Errore import ${entity.name}`);
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
      // Reset file input
      if (fileInputRefs.current[entity.id]) {
        fileInputRefs.current[entity.id].value = '';
      }
    }
  };

  const triggerFileInput = (entityId) => {
    if (fileInputRefs.current[entityId]) {
      fileInputRefs.current[entityId].click();
    }
  };

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Import/Export Dati CSV
          </CardTitle>
          <CardDescription>
            Importa ed esporta dati in formato CSV per gestire massivamente le informazioni del tuo negozio
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-2 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
            <Info className="w-5 h-5 text-blue-500 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-700 dark:text-blue-300">Come funziona:</p>
              <ol className="list-decimal list-inside mt-2 space-y-1 text-blue-600 dark:text-blue-400">
                <li>Scarica il <strong>template CSV</strong> per vedere il formato corretto</li>
                <li>Compila il file CSV con i tuoi dati (usa Excel, Google Sheets o simili)</li>
                <li>Importa il file compilato per caricare i dati</li>
                <li>Usa <strong>Esporta Dati</strong> per scaricare i dati esistenti</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entity Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {ENTITIES.map((entity) => {
          const Icon = entity.icon;
          const isExporting = loading[`export-${entity.id}`];
          const isExportingTemplate = loading[`export-${entity.id}-template`];
          const isImporting = loading[`import-${entity.id}`];
          
          return (
            <Card key={entity.id} className="overflow-hidden">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Icon className="w-5 h-5 text-primary" />
                  {entity.name}
                </CardTitle>
                <CardDescription>{entity.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Fields info */}
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium">Campi: </span>
                  {entity.fields.join(', ')}
                </div>
                
                {entity.note && (
                  <div className="text-xs p-2 bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 rounded">
                    <strong>Nota:</strong> {entity.note}
                  </div>
                )}
                
                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {/* Download Template */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExport(entity, true)}
                    disabled={isExportingTemplate}
                    data-testid={`download-template-${entity.id}`}
                  >
                    {isExportingTemplate ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <FileDown className="w-4 h-4 mr-1" />
                    )}
                    Template
                  </Button>
                  
                  {/* Export Data */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExport(entity, false)}
                    disabled={isExporting}
                    data-testid={`export-${entity.id}`}
                  >
                    {isExporting ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-1" />
                    )}
                    Esporta
                  </Button>
                  
                  {/* Import */}
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    ref={(el) => fileInputRefs.current[entity.id] = el}
                    onChange={(e) => handleImport(entity, e.target.files?.[0])}
                  />
                  <Button
                    size="sm"
                    onClick={() => triggerFileInput(entity.id)}
                    disabled={isImporting}
                    data-testid={`import-${entity.id}`}
                  >
                    {isImporting ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-1" />
                    )}
                    Importa
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Result Dialog */}
      <Dialog open={resultDialog.open} onOpenChange={(open) => setResultDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {resultDialog.result?.errors?.length > 0 ? (
                <AlertCircle className="w-5 h-5 text-amber-500" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-500" />
              )}
              {resultDialog.title}
            </DialogTitle>
          </DialogHeader>
          
          {resultDialog.result && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex gap-4">
                <div className="flex-1 p-4 bg-green-50 dark:bg-green-950 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{resultDialog.result.imported}</p>
                  <p className="text-sm text-green-600">Importati</p>
                </div>
                <div className="flex-1 p-4 bg-red-50 dark:bg-red-950 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-600">{resultDialog.result.errors?.length || 0}</p>
                  <p className="text-sm text-red-600">Errori</p>
                </div>
              </div>
              
              {/* Default password info */}
              {resultDialog.result.default_password && (
                <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>Password default:</strong> {resultDialog.result.default_password}
                  </p>
                </div>
              )}
              
              {/* Errors list */}
              {resultDialog.result.errors?.length > 0 && (
                <div className="max-h-48 overflow-y-auto">
                  <p className="text-sm font-medium mb-2">Errori:</p>
                  <ul className="text-sm space-y-1">
                    {resultDialog.result.errors.map((error, idx) => (
                      <li key={idx} className="text-red-600 dark:text-red-400 text-xs">
                        • {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <Button className="w-full" onClick={() => setResultDialog(prev => ({ ...prev, open: false }))}>
                Chiudi
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
